import React, { Component } from "react"
import PropTypes from 'prop-types'
import { Alert, Col, Container, Row } from "reactstrap"
import { connect } from "react-redux"
import { Link, withRouter } from "react-router-dom"
import { AvField, AvForm } from "availity-reactstrap-validation"
import { apiError, loginUser, verify2FA, resend2FA, clear2FA } from "../../store/actions"
import loginImg from "../../assets/img/agent-login.png"
import "../../assets/scss/login.scss"
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import FooterLogin from "components/VerticalLayout/FooterLogin"
import PasswordChangeCard from "../Dashboard/PasswordChangeModal"

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || ""

class Login extends Component {
  constructor(props) {
    super(props)
    this.state = {
      showPassword: false,
      rememberMe: false,
      email: "",
      password: "",
      lastEmail: "",
      lastPassword: "",
      resendIn: 0,
      otpChannel: "Sms",
      turnstileToken: null,
    }
    this.handleValidSubmit = this.handleValidSubmit.bind(this)
    this.handleVerifySubmit = this.handleVerifySubmit.bind(this)
    this.resendTimer = null
    this.turnstileRef = null
    this.turnstileWidgetId = null
    this.turnstileRetry = null
  }
  togglePassword = () => {
    this.setState((prev) => ({ showPassword: !prev.showPassword }));
  };
  componentDidMount() {
    this.props.apiError("")
    this.props.clear2FA()

    const preload = document.createElement("link")
    preload.rel = "preload"
    preload.as = "image"
    preload.href = loginImg
    document.head.appendChild(preload)

    if (TURNSTILE_SITE_KEY) {
      this.loadTurnstile()
    }
  }

  componentDidUpdate(prevProps) {
    if (
      TURNSTILE_SITE_KEY &&
      !this.props.twoFactor?.required &&
      prevProps.twoFactor?.required
    ) {
      this.loadTurnstile()
    }

    if (
      TURNSTILE_SITE_KEY &&
      prevProps.error !== this.props.error &&
      this.props.error &&
      !this.props.twoFactor?.required
    ) {
      this.resetTurnstile()
    }

    const prevTf = prevProps.twoFactor || {}
    const nextTf = this.props.twoFactor || {}
    if (!nextTf.required) return

    const challengeChanged = nextTf.challengeId && nextTf.challengeId !== prevTf.challengeId
    const resendChanged = nextTf.resendToken && nextTf.resendToken !== prevTf.resendToken
    if (challengeChanged || resendChanged) {
      const primary = nextTf.delivery?.primary || "Sms"
      this.setState({ otpChannel: primary })
      this.startResendCountdown(nextTf.resendAvailableInSeconds || 60)
    }
  }

  componentWillUnmount() {
    this.clearResendCountdown()
    this.destroyTurnstile()
  }

  loadTurnstile = () => {
    if (!TURNSTILE_SITE_KEY || this.props.twoFactor?.required) return

    if (window.turnstile && this.turnstileRef) {
      if (this.turnstileWidgetId != null) {
        window.turnstile.remove(this.turnstileWidgetId)
        this.turnstileWidgetId = null
      }

      this.turnstileWidgetId = window.turnstile.render(this.turnstileRef, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: "light",
        callback: (token) => this.setState({ turnstileToken: token }),
        "expired-callback": () => this.setState({ turnstileToken: null }),
        "error-callback": () => this.setState({ turnstileToken: null }),
      })
      return
    }

    this.turnstileRetry = setTimeout(this.loadTurnstile, 200)
  }

  resetTurnstile = () => {
    this.setState({ turnstileToken: null })
    if (window.turnstile && this.turnstileWidgetId != null) {
      window.turnstile.reset(this.turnstileWidgetId)
    }
  }

  destroyTurnstile = () => {
    if (this.turnstileRetry) {
      clearTimeout(this.turnstileRetry)
      this.turnstileRetry = null
    }
    if (window.turnstile && this.turnstileWidgetId != null) {
      window.turnstile.remove(this.turnstileWidgetId)
      this.turnstileWidgetId = null
    }
  }

  startResendCountdown = (seconds) => {
    this.clearResendCountdown()
    this.setState({ resendIn: Number(seconds) || 0 })
    this.resendTimer = setInterval(() => {
      this.setState((prev) => {
        if (prev.resendIn <= 1) {
          this.clearResendCountdown()
          return { resendIn: 0 }
        }
        return { resendIn: prev.resendIn - 1 }
      })
    }, 1000)
  }

  clearResendCountdown = () => {
    if (this.resendTimer) {
      clearInterval(this.resendTimer)
      this.resendTimer = null
    }
  }

  lastFour = (masked) => {
    const digits = String(masked || "").replace(/\D/g, "")
    return digits.slice(-4) || "••••"
  }

  destinationLabel = () => {
    const delivery = this.props.twoFactor?.delivery || {}
    const channel = this.state.otpChannel
    if (channel === "Email") {
      return delivery.maskedEmail || "your email"
    }
    return `Phone ending in ${this.lastFour(delivery.maskedPhone)}`
  }

  instructionText = () => {
    const channel = this.state.otpChannel
    if (channel === "Email") return "We've sent an email to:"
    if (channel === "Call") return "We're calling:"
    return "We've sent a text message to:"
  }

  handleValidSubmit(event, values) {
    if (TURNSTILE_SITE_KEY && !this.state.turnstileToken) {
      this.props.apiError("Please complete the security check.")
      return
    }

    this.setState({
      lastEmail: values.email,
      lastPassword: values.password,
    })
    this.props.loginUser(
      {
        ...values,
        rememberMe: this.state.rememberMe,
        turnstileToken: this.state.turnstileToken,
      },
      this.props.history
    )
  }

  handleVerifySubmit(event, values) {
    this.props.verify2FA(
      {
        code: values.code,
        rememberDevice: this.state.rememberMe,
      },
      this.props.history
    )
  }

  handleRememberMeChange = e => {
    this.setState({ rememberMe: e.target.checked })
  }

  handleBackToLogin = () => {
    this.clearResendCountdown()
    this.props.clear2FA()
    this.props.apiError("")
    if (TURNSTILE_SITE_KEY) {
      setTimeout(this.loadTurnstile, 0)
    }
  }

  handleResendCode = () => {
    if (this.state.resendIn > 0) return
    this.props.resend2FA(this.state.otpChannel || "Sms")
  }

  renderOtpPage = () => {
    const { rememberMe, resendIn } = this.state
    const { loading } = this.props
    const canResend = resendIn <= 0

    return (
      <div className="account-pages otp-verify-page">
        <ToastContainer autoClose={2000} />
        <div className="otp-verify-card">
          <h1 className="otp-verify-title">Verify Your Identity</h1>
          <p className="otp-verify-copy">{this.instructionText()}</p>
          <div className="otp-verify-destination">{this.destinationLabel()}</div>

          <AvForm onValidSubmit={this.handleVerifySubmit}>
            {this.props.error ? (
              <Alert color="danger">{this.props.error}</Alert>
            ) : null}
            <AvField
              name="code"
              className="form-control otp-verify-input"
              placeholder="Enter the 6-digit code*"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength="6"
              validate={{
                required: { value: true, errorMessage: "Enter the 6-digit code" },
                pattern: { value: "^[0-9]{6}$", errorMessage: "Enter a 6-digit code" },
              }}
            />
            <label className="otp-verify-remember">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={this.handleRememberMeChange}
              />
              Remember this browser for 30 days
            </label>
            <div className="d-grid">
              <button
                className="btn btn-login btn-block"
                type="submit"
                disabled={loading}
              >
                Continue
              </button>
            </div>
          </AvForm>

          <p className="otp-verify-help">
            Didn&apos;t receive a code?{" "}
            {canResend ? (
              <button type="button" className="otp-verify-link" onClick={this.handleResendCode}>
                Resend
              </button>
            ) : (
              <span>Resend in {resendIn}s</span>
            )}
          </p>

          <button type="button" className="otp-verify-back" onClick={this.handleBackToLogin}>
            Back to login
          </button>
        </div>
        <FooterLogin />
      </div>
    )
  }

  render() {
    if (this.props.twoFactor?.required) {
      return this.renderOtpPage()
    }

    if (localStorage.getItem("isChangePasswordRequired") === "true") {
      return <PasswordChangeCard />
    }

 const { showPassword, email, password, rememberMe, turnstileToken } = this.state
    const { loading } = this.props
    const turnstileRequired = Boolean(TURNSTILE_SITE_KEY)
    const canSubmit = !loading && (!turnstileRequired || Boolean(turnstileToken))
    return (
      <React.Fragment>
        <ToastContainer autoClose={2000} />
        <div className="account-pages">
          <Container>
            <Row className="justify-content-center align-items-center vh-100">
              <Col lg={11} xl={10}>
                <div className="login-shell-card overflow-hidden my-4">
                  <Row className="g-0 align-items-center">
                    <Col lg={6} md={6}>
                      <div className="login-form-panel">
                        <div className="login-heading">
                          <h1>Agent Portal</h1>
                        </div>
                        <div className="p-2">
                          <AvForm
                            className="form-horizontal"
                            method="post"
                            onValidSubmit={this.handleValidSubmit}
                          >
                            {this.props.error && this.props.error ? (
                              <Alert color="danger">{this.props.error}</Alert>
                            ) : null}
                            <div className="form-group mb-3">
                              <AvField
                                name="email"
                                label="Email Address"
                                value={email}
                                className="form-control border-r-50"
                                placeholder="Enter email"
                                type="email"
                                required
                              />
                            </div>
                            <div className="mb-3 position-relative">
                              <AvField
                                name="password"
                                label="Password"
                                value={password}
                                placeholder="Enter Password"
                                className="form-control border-r-50"
                                type={showPassword ? "text" : "password"}
                                required
                              />
                              <span
                                onClick={this.togglePassword}
                                style={{
                                  position: "absolute",
                                  top: "38px",
                                  right: "10px",
                                  cursor: "pointer",
                                  color: "#666",
                                }}
                              >
                                {showPassword ? <i className="far fa-eye" /> : <i className="far fa-eye-slash" />}
                              </span>
                            </div>
                            <div className="form-check mb-0">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id="customControlInline"
                                checked={rememberMe}
                                onChange={this.handleRememberMeChange}
                              />
                              <label
                                className="form-check-label"
                                htmlFor="customControlInline"
                              >
                                Remember me
                              </label>
                            </div>

                            {turnstileRequired ? (
                              <div
                                className="login-turnstile"
                                ref={(el) => {
                                  this.turnstileRef = el
                                }}
                              />
                            ) : null}

                            <div className="mt-3 d-grid">
                              <button
                                className="btn btn-login btn-block"
                                type="submit"
                                disabled={!canSubmit}
                              >
                                Submit
                              </button>
                            </div>

                            <div className="mt-4">
                              <Link to="/forgot-password" className="login-forgot-link">
                                <i className="mdi mdi-lock me-1" /> Forgot your
                                password?
                              </Link>
                            </div>
                          </AvForm>
                        </div>
                      </div>
                    </Col>
                    <Col lg={6} md={6} className="d-none d-md-block">
                      <div className="login-art-panel">
                        <img
                          src={loginImg}
                          className="img-fluid login-art-img"
                          alt="Agent login"
                          fetchPriority="high"
                          decoding="async"
                        />
                      </div>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
          </Container>

          <FooterLogin />
        </div>
      </React.Fragment>
    )
  }
}

Login.propTypes = {
  apiError: PropTypes.any,
  error: PropTypes.any,
  history: PropTypes.object,
  loginUser: PropTypes.func,
  verify2FA: PropTypes.func,
  resend2FA: PropTypes.func,
  clear2FA: PropTypes.func,
  twoFactor: PropTypes.object,
  loading: PropTypes.bool,
}

const mapStateToProps = state => {
  const { error, twoFactor, loading } = state.Login
  return { error, twoFactor, loading }
}

export default withRouter(
  connect(mapStateToProps, { loginUser, apiError, verify2FA, resend2FA, clear2FA })(Login)
)
